import { Box, Collapse } from '@mui/material';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp, WhereUsed } from '@sweep-io/sweep-design/dist/icons';
import { Button, Typography } from '@sweep-io/sweep-design';
import { useSweepFields } from '../../../sweep-fields/useCachedSweepFields';
import { useRunOnce } from '../../common/useRunOnce';
import { SelectorValueTypes } from '../../../types/enums/SelectorValueTypes';
import { settingsPanelLayout } from './helpers';
import { PriorityRulesTable } from './PriorityRulesTable';
import { PriorityRuleDialog } from './PriorityRuleDialog';
import { uniqueId } from '../../../lib/uniqueId';
import { DataTableId } from '../../common/table/utils';
import cloneDeep from 'lodash/cloneDeep';
import { SweepFieldOperator } from '../../SweepFieldsOperatorSelector/sweepFieldsTypesAndOperators';
import { populateFieldsInfoFromSweepFields } from '../../../sweep-fields/sweepFieldsInfoReducer';
import { useRunOnceWhenTruthy } from '../../common/useRunOnceWhenTruthy';

interface SetMasterRecordPanelProps {
  masterRecordRules?: PriorityRule[];
  readonly?: boolean;
  crmOrgId: string;
  objectType: string;
  onChange: (rules?: PriorityRule[]) => void;
}

const CREATED_DATE = 'Created Date';

const SetMasterRecordPanel = ({
  masterRecordRules,
  readonly,
  crmOrgId,
  objectType,
  onChange,
}: SetMasterRecordPanelProps) => {
  const dispatch = useDispatch();
  const { searchSweepFields } = useSweepFields();
  const [objectFields, setObjectFields] = useState<SweepField[]>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [isPanelExpanded, setIsPanelExpanded] = useState(true);

  const getObjectFields = async () => {
    const { sweepFields } = await searchSweepFields({
      objectType: [objectType],
      crmOrgId,
    });
    const fields = sweepFields[objectType].fields;
    setObjectFields(fields);
    dispatch(populateFieldsInfoFromSweepFields({ sweepFields: fields }));
  };

  useRunOnce(async () => {
    await getObjectFields();
  });

  useRunOnceWhenTruthy(() => {
    setDefaultRule();
  }, !!objectFields);

  const createDefaultRule = useCallback((): PriorityRule => {
    const currentField = objectFields?.find((el) => el.sweepFieldName === CREATED_DATE);

    if (currentField && currentField.id) {
      return {
        _id: uniqueId(),
        name: 'Master record rule for ' + objectType,
        when: {
          criteriaLogic: '1',
          criteria: [
            {
              criterionId: currentField.id,
              _fieldIds: [currentField.id],
              operator: SweepFieldOperator.MIN,
              value: 'true',
              valueType: SelectorValueTypes.LITERAL,
              fieldType: currentField.sfDisplayType,
            },
          ],
        },
      };
    }

    return {
      _id: uniqueId(),
      name: 'Master record rule for ' + objectType,
      when: {
        criteriaLogic: '1',
        criteria: [],
      },
    };
  }, [objectFields, objectType]);

  const setDefaultRule = useCallback(() => {
    if (!masterRecordRules && objectFields) {
      const defaultRule = createDefaultRule();
      onChange([defaultRule]);
    }
  }, [masterRecordRules, objectFields, createDefaultRule, onChange]);

  const handleNewRule = () => {
    setEditingRuleId(null);
    setDialogOpen(true);
  };

  const handleEditRule = (ruleId: string) => {
    setEditingRuleId(ruleId);
    setDialogOpen(true);
  };

  const handleDuplicateRule = (ruleId: string) => {
    const ruleToDuplicate = masterRecordRules?.find((r) => r._id === ruleId);
    if (ruleToDuplicate) {
      const duplicatedRule: PriorityRule = {
        ...cloneDeep(ruleToDuplicate),
        _id: uniqueId(),
        name: `${ruleToDuplicate.name} (copy)`,
      };
      onChange([...(masterRecordRules ?? []), duplicatedRule]);
    }
  };

  const handleSaveRule = (rule: PriorityRule) => {
    if (editingRuleId) {
      const updatedRules = (masterRecordRules ?? []).map((r) =>
        r._id === editingRuleId ? rule : r,
      );
      onChange(updatedRules);
    } else {
      onChange([...(masterRecordRules ?? []), rule]);
    }
    setDialogOpen(false);
    setEditingRuleId(null);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setEditingRuleId(null);
  };

  const editingRule = editingRuleId
    ? masterRecordRules?.find((r) => r._id === editingRuleId)
    : undefined;

  return (
    <Box sx={{ ...settingsPanelLayout }}>
      <Collapse in={isPanelExpanded} collapsedSize={24}>
        <Box className="panelHeader" onClick={() => setIsPanelExpanded(!isPanelExpanded)}>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <WhereUsed />
            <Box sx={{ display: 'flex', flexDirection: 'column', marginTop: '2px' }}>
              <Typography variant="body-bold">Master record settings</Typography>
              <Typography variant="body">
                Define the criteria to determine default record to take values when merging records
              </Typography>
            </Box>
          </Box>
          <Box>{isPanelExpanded ? <ChevronUp size="medium" /> : <ChevronDown size="medium" />}</Box>
        </Box>
        <Box sx={{ marginTop: '16px', marginLeft: '44px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <PriorityRulesTable
              rules={masterRecordRules ?? []}
              onRulesChange={onChange}
              onRuleEdit={handleEditRule}
              onRuleDuplicate={handleDuplicateRule}
              readonly={readonly}
              minRules={0}
              tableId={DataTableId.MasterRecordRulesTable}
            />
            {!readonly && (
              <Box>
                <Button
                  variant="flat-blue"
                  size="small"
                  startIconName="Plus"
                  onClick={handleNewRule}
                >
                  New rule
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
      {dialogOpen && (
        <PriorityRuleDialog
          initialRule={editingRule}
          onCreateEdit={handleSaveRule}
          crmOrgId={crmOrgId}
          objectType={objectType}
          onCancel={handleCancel}
          defaultRuleName={'Master record rule for ' + objectType}
        />
      )}
    </Box>
  );
};

export default SetMasterRecordPanel;
